import React, { ReactNode, useCallback, useMemo, useState } from 'react'
import { FormSection } from '../../../common/components/SectionedForm'
import { api, buildPaginatedUrl } from '../../../common/api'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ServerPage, VpUser } from '../../../app/types'
import PersonIcon from '@mui/icons-material/Person'
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link } from '@mui/material'
import { Sort, useInfiniteScroll } from '../../../common/components/table/useInfiniteScroll'
import { usePrivilege, useUrlHelpers } from '../../../common/util/hooks'
import { FeaturePrivileges } from '../../../common/util/featurePrivileges'
import { ColumnDefinition, InfiniteScrollTable } from '../../../common/components/table/Table'
import TableRowActionsMenu from '../../../common/components/table/TableRowActionsMenu'
import { sorterHeader } from '../../../common/components/table/SorterHeader'
import { AddButton } from '../../../common/components/AddButton'
import CancelButton from '../../../common/form/CancelButton'
import useAdminVpUserForm from '../useAdminVpUserForm'
import ErrorAlert from '../../../common/components/ErrorAlert'
import DeletionModal from '../../../common/components/DeletionModal'
import { SearchField } from '../../../common/components/SearchField'
import { customerOriginIdSelector } from '../../../authentication/redux'
import { useHistory } from 'react-router'
import { vpUserDetailUrl } from '../index'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'

export const useUsersSection = (): FormSection<VpUser[]> => {
    const { t } = useTranslation()
    const { createEnterpriseUrl } = useUrlHelpers()
    const customerOriginId = useSelector(customerOriginIdSelector)
    const [entryToDelete, setEntryToDelete] = useState<VpUser | null>(null)
    const [entryToUpdate, setEntryToUpdate] = useState<VpUser | null>(null)
    const { hasAccess: hasWriteAccess, onlyWithAccess: onlyWithWriteAccess } = usePrivilege(FeaturePrivileges.enterpriseAdminMgmt.write)
    const history = useHistory()

    const fetchEntries = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<VpUser>> => {
            const { data } = await api.get(buildPaginatedUrl(createEnterpriseUrl('vpUsers'), pageParam, sortParam, searchTermParam))
            return data
        },
        [createEnterpriseUrl],
    )

    const { sort, setSort, setSearchTerm, rows, error, fetchNextPage, hasNextPage, isLoading, refetch } = useInfiniteScroll<VpUser>({
        queryKey: `admin-management-users-${customerOriginId || 'n/a'}`,
        initialSort: { field: 'lastName', order: 'asc' },
        fetchFunction: fetchEntries,
    })

    const onSubmit = useCallback(
        async (formData: any) => {
            const url = createEnterpriseUrl(`vpUsers${entryToUpdate?.id ? `/${entryToUpdate.id}` : ''}`)
            if (entryToUpdate?.id) {
                await api.put(url, formData)
                setEntryToUpdate(null)
                await refetch()
            } else {
                const { data: created } = await api.post(url, formData)
                setEntryToUpdate(null)
                history.push(vpUserDetailUrl.replace(':id', created.id) + '?#1')
            }
        },
        [refetch, entryToUpdate, createEnterpriseUrl, history],
    )

    const { handleSubmit, form: Form, submitButton: SubmitButton, reset } = useAdminVpUserForm({ entry: entryToUpdate, onSubmit })

    const rowActions = useMemo(
        () => [
            {
                key: 'edit',
                text: t('general.general'),
                onClick: (record: VpUser) => {
                    history.push(vpUserDetailUrl.replace(':id', record.id) + (hasWriteAccess ? '?edit=true#0' : '?#0'))
                },
                icon: () => <PersonIcon />,
            },
            {
                key: 'manage-profiles',
                text: t('admin-management.profiles-section'),
                onClick: (record: VpUser) => {
                    history.push(vpUserDetailUrl.replace(':id', record.id) + '?#1')
                },
                icon: () => <AdminPanelSettingsIcon />,
                dividerBeneath: true,
            },
            {
                key: 'delete',
                text: t('general.delete'),
                onClick: (record: VpUser) => {
                    setEntryToDelete(record)
                },
                needsWriteAccess: true,
            },
        ],
        [t, history, hasWriteAccess],
    )

    // @ts-ignore
    const columns: ColumnDefinition<VpUser>[] = useMemo(
        () => [
            {
                title: t('admin-management.users.columns.name'),
                key: 'lastName',
                sorter: true,
                render: (text: string, record: VpUser) => <Link href={vpUserDetailUrl.replace(':id', record.id)}>{`${record.lastName} ${record.firstName}`}</Link>,
            },
            {
                title: t('admin-management.users.columns.email'),
                key: 'email',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('admin-management.users.columns.roles'),
                key: 'profiles',
                sorter: false,
                render: (_: any, record: VpUser) => <>{record.profiles?.map(p => p.roleName).join(', ') || ''}</>,
            },
            {
                title: '',
                key: 'id',
                sorter: false,
                render: (text: string, record: VpUser) => <TableRowActionsMenu record={record} menuItems={rowActions} readOnly={!hasWriteAccess} />,
            },
        ],
        [t, rowActions, hasWriteAccess],
    )

    const onSearch = useCallback(async (searchTerm: string) => setSearchTerm(searchTerm), [setSearchTerm])
    const sorter = useCallback(col => sorterHeader(col, sort, setSort), [sort, setSort])
    const getRowKey = useCallback(entry => entry.id, [])
    const getDeleteUrl = useCallback(i => i ? createEnterpriseUrl(`vpUsers/${i.id}`) : null, [createEnterpriseUrl])

    const buttonPanel = useMemo(
        () => onlyWithWriteAccess(
            <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={2} flexWrap={'nowrap'}>
                <Grid item>
                    <AddButton
                        onClick={() => {
                            reset()
                            setEntryToUpdate({ lastName: '', firstName: '', email: '', id: '' })
                            return null
                        }}
                        label={`${t('admin-management.create-user')}`}
                    />
                </Grid>
            </Grid>,
        ),
        [t, reset, onlyWithWriteAccess],
    )

    const entryForm = useMemo(
        () => (
            <form className={'ant-form ant-form-vertical'} onSubmit={handleSubmit} noValidate={true}>
                <Dialog disablePortal open={!!entryToUpdate} onClose={() => setEntryToUpdate(null)}>
                    <DialogTitle>{t(entryToUpdate?.id ? 'admin-management.edit-user' : 'admin-management.create-user')}</DialogTitle>
                    <DialogContent>
                        <Form />
                    </DialogContent>
                    <DialogActions>
                        <SubmitButton />
                        <CancelButton onClick={() => setEntryToUpdate(null)} />
                    </DialogActions>
                </Dialog>
            </form>
        ),
        [Form, SubmitButton, entryToUpdate, handleSubmit, t],
    )

    const renderDetails = useCallback(
        (data: VpUser[]): ReactNode => {
            return (
                <Grid container p={1}>
                    <Grid container xs={12} mb={3} item justifyContent={'flex-start'} flexDirection={'row'} flexWrap={'nowrap'}>
                        <Grid item>{buttonPanel}</Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <SearchField onSearch={onSearch} />
                    </Grid>
                    <ErrorAlert showAlert={!!error} />
                    <Grid item xs={12}>
                        <InfiniteScrollTable<VpUser>
                            hasNextPage={hasNextPage}
                            data={rows}
                            fetchNextPage={fetchNextPage}
                            isFetching={false}
                            getRowKey={getRowKey}
                            sorter={sorter}
                            columns={columns}
                            tableId={'adminUsersTable'}
                        />
                    </Grid>
                    <DeletionModal<VpUser | null>
                        nameToDelete={user => `${user?.lastName} ${user?.firstName}`}
                        itemToDelete={entryToDelete} deleteUrl={getDeleteUrl} onClose={() => setEntryToDelete(null)} onSuccess={refetch} />
                    {entryForm}
                </Grid>
            )
        },
        [columns, buttonPanel, entryForm, entryToDelete, error, fetchNextPage, getDeleteUrl, getRowKey, hasNextPage, onSearch, refetch, rows, sorter],
    )

    return {
        title: t('admin-management.users-section'),
        data: rows,
        isFetching: isLoading || false,
        error: error,
        refetch: refetch,
        renderDetails: renderDetails,
        renderForm: null,
        enabled: () => true,
        icon: () => <PersonIcon />,
    }
}
